import { BehaviorSubject, Observable } from 'rxjs';

/**
 * A utility class for managing filter state with `applied` vs `draft` values.
 *
 * - `applied`: The last confirmed filters (committed by `apply`).
 * - `draft`: The working copy (updated live when user interacts with filter UI).
 * - `isOpen`: Whether the filter modal/panel is currently open.
 *
 * Supports actions:
 * - `openFilter` → syncs `draft` with `applied` and sets `isOpen = true`.
 * - `closeFilter` → closes the filter modal without applying.
 * - `updateDraft` → updates the `draft` state incrementally.
 * - `apply` → commits `draft` → `applied` and closes modal.
 * - `cancel` → discards `draft` and reverts to `applied`.
 * - `reset` → resets both `applied` and `draft` back to initial.
 *
 * @typeParam T - Shape of the filter object
 *
 * @example
 * // Define filter type
 * interface ProductFilters {
 *   search: string;
 *   category: string | null;
 * }
 *
 * // Create a new instance
 * const filterState = new FilterState<ProductFilters>({
 *   search: '',
 *   category: null,
 * });
 *
 * // Open filter panel
 * filterState.openFilter();
 * console.log(filterState.isOpen); // true
 *
 * // Update draft
 * filterState.updateDraft({ search: 'laptop' });
 * console.log(filterState.draft.search); // "laptop"
 *
 * // Apply changes
 * filterState.apply();
 * console.log(filterState.applied.search); // "laptop"
 *
 * // Cancel changes
 * filterState.updateDraft({ search: 'phone' });
 * filterState.cancel();
 * console.log(filterState.draft.search); // "laptop" (discarded)
 *
 * // Reset everything
 * filterState.reset();
 * console.log(filterState.applied); // { search: '', category: null }
 */
export class FilterState<T extends object> {
  private state$ = new BehaviorSubject<{
    applied: T;
    draft: T;
    isOpen: boolean;
  }>({ applied: {} as T, draft: {} as T, isOpen: false });

  /**
   * Create a new filter state instance.
   *
   * @param initial - The initial values of the filter (used for reset).
   */
  constructor(private initial: T) {
    this.state$.next({
      applied: initial,
      draft: initial,
      isOpen: false,
    });
  }

  /**
   * Reactive observable stream of the entire state.
   * Useful with Angular `async` pipe.
   *
   * @example
   * filterState.state.subscribe(s => {
   *   console.log(s.applied, s.draft, s.isOpen);
   * });
   */
  get state(): Observable<{ applied: T; draft: T; isOpen: boolean }> {
    return this.state$.asObservable();
  }

  /**
   * Current applied filters (synchronous).
   *
   * @example
   * console.log(filterState.applied);
   */
  get applied(): T {
    return this.state$.value.applied;
  }

  /**
   * Current draft filters (synchronous).
   *
   * @example
   * console.log(filterState.draft);
   */
  get draft(): T {
    return this.state$.value.draft;
  }

  get initialValue(): T {
    return this.initial;
  }

  /**
   * Whether the filter panel is open.
   *
   * @example
   * if (filterState.isOpen) { ... }
   */
  get isOpen(): boolean {
    return this.state$.value.isOpen;
  }

  /**
   * Opens the filter panel and syncs draft with applied.
   *
   * @example
   * filterState.openFilter();
   */
  openFilter = (): void => {
    const s = this.state$.value;
    this.state$.next({ ...s, draft: s.applied, isOpen: true });
  };

  /**
   * Closes the filter panel without changing values.
   *
   * @example
   * filterState.closeFilter();
   */
  closeFilter = (): void => {
    const s = this.state$.value;
    this.state$.next({ ...s, isOpen: false });
  };

  /**
   * Updates the draft filter values incrementally.
   *
   * @param changes - Partial object of changes
   *
   * @example
   * filterState.updateDraft({ search: 'phone' });
   * console.log(filterState.draft.search); // "phone"
   */
  updateDraft = (changes: Partial<T>): void => {
    const s = this.state$.value;
    this.state$.next({ ...s, draft: { ...s.draft, ...changes } });
  };

  /**
   * Commits draft → applied and closes the panel.
   *
   * @example
   * filterState.apply();
   * console.log(filterState.applied); // equals previous draft
   */
  apply = (): void => {
    const s = this.state$.value;
    this.state$.next({ ...s, applied: s.draft, isOpen: false });
  };

  /**
   * Discards changes and reverts draft back to applied.
   *
   * @example
   * filterState.updateDraft({ search: 'temp' });
   * filterState.cancel();
   * console.log(filterState.draft.search); // back to applied value
   */
  cancel = (): void => {
    const s = this.state$.value;
    this.state$.next({ ...s, draft: s.applied, isOpen: false });
  };

  /**
   * Resets both applied and draft filters back to initial values.
   *
   * @example
   * filterState.reset();
   * console.log(filterState.applied); // { ...initial }
   */
  reset = (): void => {
    this.state$.next({
      applied: this.initial,
      draft: this.initial,
      isOpen: false,
    });
  };

  /**
 * Returns the count of active (non-default) filters.
 *
 * Compares the current `applied` filters against the `initial` values.
 *
 * @example
 * const count = filterState.getFilterCount();
 * console.log(count); // e.g. 2
 */
  getFilterCount = (): number => {
    const applied = this.state$.value.applied;
    let count = 0;

    for (const key in applied) {
      if (!Object.prototype.hasOwnProperty.call(applied, key)) continue;

      const value = (applied as any)[key];
      const initialValue = (this.initial as any)[key];

      // Compare primitive or shallow objects
      const isDifferent =
        typeof value === 'object'
          ? JSON.stringify(value) !== JSON.stringify(initialValue)
          : value !== initialValue;

      if (isDifferent) count++;
    }

    return count;
  };
}
